import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import api from '../api';

const CurrentWeekContext = createContext();

export const CurrentWeekProvider = ({ children }) => {
  const [currentWeek, setCurrentWeek] = useState(null);
  const [calendar, setCalendar] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const setCurrentWeekMemoized = useCallback((week) => {
    setCurrentWeek(prevWeek => {
      if (prevWeek !== week) {
        return week;
      }
      return prevWeek;
    });
  }, []);

  const fetchCurrentWeekAndCalendar = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [weekResponse, calendarResponse] = await Promise.all([
        api.fetchCurrentWeek(),
        api.fetchCalendar()
      ]);
      const weekData = weekResponse.data.currentWeek;
      const newWeek = weekData ? weekData.week : null;
      const newCalendar = calendarResponse.data;

      setCurrentWeek(prevWeek => prevWeek !== newWeek ? newWeek : prevWeek);
      setCalendar(prevCalendar => {
        if (JSON.stringify(prevCalendar) !== JSON.stringify(newCalendar)) {
          return newCalendar;
        }
        return prevCalendar;
      });
    } catch (error) {
      console.error('Failed to fetch current week and calendar:', error);
      setError('Failed to fetch current week and calendar');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCurrentWeekAndCalendar();
  }, [fetchCurrentWeekAndCalendar]);

  const value = useMemo(() => ({
    currentWeek,
    calendar,
    isLoading,
    error,
    setCurrentWeek: setCurrentWeekMemoized,
    fetchCurrentWeekAndCalendar
  }), [currentWeek, calendar, isLoading, error, setCurrentWeekMemoized, fetchCurrentWeekAndCalendar]);

  return (
    <CurrentWeekContext.Provider value={value}>
      {children}
    </CurrentWeekContext.Provider>
  );
};

CurrentWeekProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useCurrentWeek = () => {
  const context = useContext(CurrentWeekContext);
  if (context === undefined) {
    throw new Error('useCurrentWeek must be used within a CurrentWeekProvider');
  }
  return context;
};
